import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BusinessAnalytics = ({ className = '' }) => {
  const [timeRange, setTimeRange] = useState('week');

  const analyticsData = {
    week: {
      recoveredIncome: 2450,
      kgSaved: 18.5,
      wasteReduction: 23,
      localRanking: 3,
      totalBusinesses: 15,
      orders: 12,
      avgRating: 4.7,
      repeatCustomers: 8
    },
    month: {
      recoveredIncome: 9800,
      kgSaved: 74.2,
      wasteReduction: 28,
      localRanking: 2,
      totalBusinesses: 15,
      orders: 48,
      avgRating: 4.8,
      repeatCustomers: 32
    },
    year: {
      recoveredIncome: 45600,
      kgSaved: 342.8,
      wasteReduction: 35,
      localRanking: 1,
      totalBusinesses: 15,
      orders: 234,
      avgRating: 4.9,
      repeatCustomers: 156
    }
  };

  const currentData = analyticsData?.[timeRange];

  const impactMetrics = [
    {
      id: 'income',
      label: 'Ingresos Recuperados',
      value: `$${currentData?.recoveredIncome?.toLocaleString()}`,
      change: '+12%',
      changeType: 'positive',
      icon: 'DollarSign',
      color: 'var(--color-success)',
      description: 'vs período anterior'
    },
    {
      id: 'weight',
      label: 'Kilogramos Salvados',
      value: `${currentData?.kgSaved} kg`,
      change: '+8%',
      changeType: 'positive',
      icon: 'Scale',
      color: 'var(--color-primary)',
      description: 'comida rescatada'
    },
    {
      id: 'waste',
      label: 'Reducción de Desperdicio',
      value: `${currentData?.wasteReduction}%`,
      change: '+5%',
      changeType: 'positive',
      icon: 'Leaf',
      color: 'var(--color-accent)',
      description: 'vs mes anterior'
    },
    {
      id: 'ranking',
      label: 'Posición Local',
      value: `#${currentData?.localRanking}`,
      change: currentData?.localRanking === 1 ? 'Líder' : `de ${currentData?.totalBusinesses}`,
      changeType: 'neutral',
      icon: 'Trophy',
      color: 'var(--color-warning)',
      description: 'en tu área'
    }
  ];

  const performanceMetrics = [
    {
      label: 'Pedidos Completados',
      value: currentData?.orders,
      icon: 'ShoppingBag',
      trend: '+15%'
    },
    {
      label: 'Calificación Promedio',
      value: currentData?.avgRating,
      icon: 'Star',
      trend: '+0.2'
    },
    {
      label: 'Clientes Recurrentes',
      value: currentData?.repeatCustomers,
      icon: 'Users',
      trend: '+25%'
    }
  ];

  const timeRangeOptions = [
    { value: 'week', label: 'Esta Semana' },
    { value: 'month', label: 'Este Mes' },
    { value: 'year', label: 'Este Año' }
  ];

  const getChangeColor = (type) => {
    switch (type) {
      case 'positive':
        return 'text-success';
      case 'negative':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Análisis de Impacto
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Seguimiento de tu rendimiento y impacto ambiental
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          {timeRangeOptions?.map(option => (
            <Button
              key={option?.value}
              variant={timeRange === option?.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeRange(option?.value)}
            >
              {option?.label}
            </Button>
          ))}
        </div>
      </div>
      {/* Main Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {impactMetrics?.map(metric => (
          <div key={metric?.id} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                <Icon name={metric?.icon} size={24} color={metric?.color} />
              </div>
              <div className={`text-sm font-medium ${getChangeColor(metric?.changeType)}`}>
                {metric?.change}
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-2xl font-heading font-bold text-foreground">
                {metric?.value}
              </h3>
              <p className="text-sm font-medium text-foreground">
                {metric?.label}
              </p>
              <p className="text-xs text-muted-foreground">
                {metric?.description}
              </p>
            </div>
          </div>
        ))}
      </div>
      {/* Performance Overview */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Rendimiento General
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {performanceMetrics?.map((metric, index) => (
            <div key={index} className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={metric?.icon} size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <span className="text-xl font-heading font-bold text-foreground">
                    {metric?.value}
                  </span>
                  <span className="text-sm font-medium text-success">
                    {metric?.trend}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {metric?.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Environmental Impact Details */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Impacto Ambiental Detallado
          </h3>
          <Button variant="outline" size="sm">
            <Icon name="Download" size={14} className="mr-2" />
            Exportar Reporte
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Droplets" size={20} color="var(--color-primary)" />
                <span className="font-medium text-foreground">Agua Ahorrada</span>
              </div>
              <span className="text-lg font-bold text-foreground">
                {(currentData?.kgSaved * 2.5)?.toFixed(1)}L
              </span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Wind" size={20} color="var(--color-success)" />
                <span className="font-medium text-foreground">CO₂ Evitado</span>
              </div>
              <span className="text-lg font-bold text-foreground">
                {(currentData?.kgSaved * 1.8)?.toFixed(1)}kg
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Users" size={20} color="var(--color-accent)" />
                <span className="font-medium text-foreground">Familias Alimentadas</span>
              </div>
              <span className="text-lg font-bold text-foreground">
                {Math.floor(currentData?.kgSaved / 2.3)}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Recycle" size={20} color="var(--color-warning)" />
                <span className="font-medium text-foreground">Desperdicio Evitado</span>
              </div>
              <span className="text-lg font-bold text-foreground">
                {currentData?.wasteReduction}%
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Award" size={20} color="var(--color-success)" />
            <div>
              <h4 className="font-medium text-success mb-1">
                ¡Excelente trabajo!
              </h4>
              <p className="text-sm text-success/80">
                Tu negocio ha evitado el desperdicio de {currentData?.kgSaved}kg de comida, 
                equivalente a alimentar {Math.floor(currentData?.kgSaved / 2.3)} familias durante un día.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessAnalytics;